import { useEffect, useState } from "react";
import Api from "../../../../shared/api/api";
import "../styles/AdminMerch.css";
import { DateTime } from "luxon";

function convertToUtc(localDatetime) {
  if (!localDatetime) return null;
  return DateTime.fromISO(localDatetime, { zone: "America/Los_Angeles" })
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

export default function AdminMerch() {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [product, setProduct] = useState({});
  const [shareProduct, setShareProduct] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const [fbBody, setFbBody] = useState("");
  const [fbFirstComment, setFbFirstComment] = useState("");
  const [igBody, setIgBody] = useState("");
  const [igFirstComment, setIgFirstComment] = useState("");
  const [twBody, setTwBody] = useState("");
  const [fbScheduledAt, setFbScheduledAt] = useState("");
  const [igScheduledAt, setIgScheduledAt] = useState("");
  const [twScheduledAt, setTwScheduledAt] = useState("");

  const defaultProduct = {
    title: "",
    description: "",
    price: "",
    image_url: "",
    is_active: true,
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await Api.getAllMerchProducts();
      setProducts(res.products || []);
    } catch (err) {
      console.error("Failed to fetch products:", err);
    }
  };

  const handleSync = async () => {
    try {
      await Api.syncMerchProducts();
      alert("✅ Synced Printify products successfully.");
      fetchProducts();
    } catch (err) {
      console.error("Sync failed:", err);
      alert("❌ Failed to sync Printify products.");
    }
  };

  const handleEdit = (existing) => {
    setProduct({ ...defaultProduct, ...existing });
    setShowModal(true);
  };

  const handleToggleActive = async (p) => {
    try {
      await Api.toggleMerchProductVisibility(p.id, !p.is_active);
      await fetchProducts();
    } catch (err) {
      console.error("Error toggling product:", err);
      alert("❌ Failed to update visibility.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await Api.deleteMerchProduct(id);
      await fetchProducts();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("❌ Failed to delete product.");
    }
  };

  const handleSave = async () => {
    try {
      await Api.updateMerchProduct(product.id, {
        title: product.title,
        description: product.description,
        price: product.price,
        image_url: product.image_url,
        is_active: product.is_active,
      });
      alert("✅ Product updated.");
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      console.error("Save failed:", err);
      alert("❌ Failed to save product.");
    }
  };

  const handleShare = (p) => {
    const productUrl = `https://kennethlpowell.com/shop/p/${p.id}`;
    setShareProduct(p);
    setFbBody("");
    setFbFirstComment(productUrl);
    setIgBody("");
    setIgFirstComment(productUrl);
    setTwBody(productUrl);
    setFbScheduledAt("");
    setIgScheduledAt("");
    setTwScheduledAt("");
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareProduct(null);
    setFbBody("");
    setFbFirstComment("");
    setIgBody("");
    setIgFirstComment("");
    setTwBody("");
    setFbScheduledAt("");
    setIgScheduledAt("");
    setTwScheduledAt("");
  };

  const handleSubmitShare = async () => {
    if (!shareProduct) return;

    const imageUrl = shareProduct.image_url || null;

    try {
      if (fbBody.trim()) {
        await Api.createSocialPost({
          content: fbBody,
          image: imageUrl,
          platform: "facebook",
          hashtags: "",
          tags: "",
          first_comment: fbFirstComment,
          scheduled_at: convertToUtc(fbScheduledAt),
          status: fbScheduledAt ? "scheduled" : "draft",
        });
      }

      if (igBody.trim()) {
        await Api.createSocialPost({
          content: igBody,
          image: imageUrl,
          platform: "instagram",
          hashtags: "",
          tags: "",
          first_comment: igFirstComment,
          scheduled_at: convertToUtc(igScheduledAt),
          status: igScheduledAt ? "scheduled" : "draft",
        });
      }

      if (twBody.trim()) {
        await Api.createSocialPost({
          content: twBody,
          image: null,
          platform: "twitter",
          hashtags: "",
          tags: "",
          scheduled_at: convertToUtc(twScheduledAt),
          status: twScheduledAt ? "scheduled" : "draft",
        });
      }

      alert("✅ Social posts created.");
      closeShareModal();
    } catch (err) {
      console.error("Error sharing product:", err);
      alert("❌ Failed to create social posts.");
    }
  };

  return (
    <div className="adminPageContainer">
      <div className="adminTopBarCentered">
        <h1>Manage Merch</h1>
        <button className="syncButton" onClick={handleSync}>
          Sync Printify Products
        </button>
      </div>
      <div className="adminBlogsList">
        {products.map((p) => (
          <div className="adminBlogRow" key={p.id}>
            <img
              className="adminBlogThumb"
              src={p.image_url || "https://via.placeholder.com/120x180?text=No+Image"}
              alt={p.title}
            />
            <div className="adminBlogInfo">
              <h3>{p.title}</h3>
              <p>${p.price}</p>
              <p className={p.is_active ? "activeLabel" : "inactiveLabel"}>
                {p.is_active ? "Active" : "Inactive"}
              </p>
            </div>
            <div className="adminMerchActions">
              <button className="adminMerchEditBtn" onClick={() => handleEdit(p)}>
                Edit
              </button>
              <button className="adminMerchDeleteBtn" onClick={() => handleDelete(p.id)}>
                Delete
              </button>
              <button className="adminMerchShareBtn" onClick={() => handleShare(p)}>
                Share
              </button>
              <button
                className={`adminMerchToggleBtn ${
                  p.is_active ? "deactivate" : "activate"
                }`}
                onClick={() => handleToggleActive(p)}
              >
                {p.is_active ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* EDIT MODAL */}
      {showModal && (
        <div className="modalOverlay" onClick={() => setShowModal(false)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Product</h2>

            <label>Title</label>
            <input
              type="text"
              value={product.title}
              onChange={(e) => setProduct({ ...product, title: e.target.value })}
            />

            <label>Description</label>
            <textarea
              value={product.description}
              onChange={(e) =>
                setProduct({ ...product, description: e.target.value })
              }
            />

            <label>Base Price</label>
            <input
              type="number"
              value={product.price}
              onChange={(e) => setProduct({ ...product, price: e.target.value })}
            />

            <label>Image URL</label>
            <input
              type="text"
              value={product.image_url}
              onChange={(e) =>
                setProduct({ ...product, image_url: e.target.value })
              }
            />

            <label>
              <input
                type="checkbox"
                checked={product.is_active}
                onChange={(e) =>
                  setProduct({ ...product, is_active: e.target.checked })
                }
              />{" "}
              Active
            </label>

            <div className="buttonRow">
              <button className="publishingSubmitBtn" onClick={handleSave}>
                Save
              </button>
              <button className="cancelBtn" onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SHARE MODAL */}
      {showShareModal && (
        <div className="modalOverlay" onClick={closeShareModal}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h2>Share Product</h2>
            <div className="platformsContainer">
              <div className="formSection">
                <h3>Facebook</h3>
                <textarea
                  value={fbBody}
                  onChange={(e) => setFbBody(e.target.value)}
                />
                <input
                  type="datetime-local"
                  value={fbScheduledAt}
                  onChange={(e) => setFbScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="First comment or link"
                  value={fbFirstComment}
                  onChange={(e) => setFbFirstComment(e.target.value)}
                />
              </div>
              <div className="formSection">
                <h3>Instagram</h3>
                <textarea
                  value={igBody}
                  onChange={(e) => setIgBody(e.target.value)}
                />
                <input
                  type="datetime-local"
                  value={igScheduledAt}
                  onChange={(e) => setIgScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="First comment or link"
                  value={igFirstComment}
                  onChange={(e) => setIgFirstComment(e.target.value)}
                />
              </div>
              <div className="formSection">
                <h3>Twitter/X</h3>
                <textarea
                  value={twBody}
                  onChange={(e) => setTwBody(e.target.value)}
                  maxLength="280"
                />
                <p>{twBody.length} / 280</p>
                <input
                  type="datetime-local"
                  value={twScheduledAt}
                  onChange={(e) => setTwScheduledAt(e.target.value)}
                />
              </div>
            </div>
            <div className="buttonRow">
              <button  onClick={handleSubmitShare}>
                Post
              </button>
              <button className="cancelBtn" onClick={closeShareModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
